/**
 * Possible alignment options for the popover relative to the trigger element.
 * - 'start': Aligns to the start edge of the trigger element
 * - 'center': Centers the popover on the trigger element
 * - 'end': Aligns to the end edge of the trigger element
 */
export const alignmentOptions = ['start', 'center', 'end'] as const;
export type Alignment = (typeof alignmentOptions)[number];

/**
 * Possible placement options for the popover relative to the trigger element.
 * - 'top': Places the popover above the trigger element
 * - 'bottom': Places the popover below the trigger element
 * - 'left': Places the popover to the left of the trigger element
 * - 'right': Places the popover to the right of the trigger element
 */
export const placementOptions = ['top', 'right', 'bottom', 'left'] as const;
export type Placement = (typeof placementOptions)[number];

/**
 * Offset configuration for popover positioning.
 * Can be a single number (applied to both x and y axes) or an object with specific x/y values.
 */
export type Offset = number | { x?: number; y?: number };

/**
 * Represents a position with x and y coordinates.
 */
export interface Position {
  x: number;
  y: number;
}

/**
 * Represents the dimensions and position of an element.
 */
export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

/**
 * Result of positioning calculations including final position, flip state and the
 * space the popover may occupy without leaving the viewport.
 */
export interface PositioningResult {
  position: Position;
  flip: {
    placement: Placement | null;
  };
  /**
   * Space the popover may occupy on its final side (after any flip), never negative.
   * On the main axis it is the room between the trigger's edge and the viewport edge,
   * less `viewportMargin` and `offset`; on the cross axis it is the viewport size less
   * `viewportMargin` on both ends. Consumers cap the popover's size with it so a surface
   * that is bigger than the room on its side shrinks instead of overflowing the viewport.
   */
  available: {
    width: number;
    height: number;
  };
}

/**
 * Additional configuration options for positioning calculations
 */
export interface PositioningOptions {
  /** Additional offset to apply to the position */
  offset?: Offset;
  /** Minimum distance to maintain from viewport edges */
  viewportMargin?: number;
  /**
   * Keeps the popover on `placement` even when it does not fit there, so only
   * the cross axis is constrained. For a pass that re-places a surface whose
   * side an earlier pass already chose, and must not undo that choice.
   */
  lockPlacement?: boolean;
}

/**
 * Extracts the x and y offset values from the Offset type.
 * Standardizes the offset format for easier usage in calculations.
 *
 * @param offset - The offset configuration (number or object)
 * @returns An object with x and y offset values
 */
const getOffsetValues = (offset: Offset = 0): { x: number; y: number } => {
  if (typeof offset === 'number') {
    return { x: offset, y: offset };
  }
  return { x: offset?.x ?? 0, y: offset?.y ?? 0 };
};

/**
 * Calculates the initial position of the popover based on the trigger element's position,
 * alignment, placement, and offset.
 *
 * This function handles the basic positioning logic without considering viewport constraints
 * or collision detection. The returned position is in document coordinates (includes scroll offset).
 *
 * @param triggerRect - The bounding rectangle of the trigger element
 * @param popoverRect - The bounding rectangle of the popover element
 * @param placement - Where to place the popover relative to the trigger
 * @param align - How to align the popover with the trigger
 * @param options - Additional configuration options
 * @returns The calculated position for the popover in document coordinates
 */
export const calculateInitialPositionOfPopover = (
  triggerRect: Rect,
  popoverRect: Rect,
  placement: Placement,
  align: Alignment,
  options: PositioningOptions = {},
): Position => {
  const { offset = 0 } = options;

  const {
    top: triggerTop,
    left: triggerLeft,
    width: triggerWidth,
    height: triggerHeight,
  } = triggerRect;
  const { width: popoverWidth, height: popoverHeight } = popoverRect;

  const { x: offsetX, y: offsetY } = getOffsetValues(offset);

  const adjustedTriggerTop = triggerTop + window.scrollY;
  const adjustedTriggerLeft = triggerLeft + window.scrollX;

  let x = 0;
  let y = 0;

  switch (placement) {
    case 'top':
    case 'bottom': {
      switch (align) {
        case 'start':
          x = adjustedTriggerLeft;
          break;
        case 'end':
          x = adjustedTriggerLeft + triggerWidth - popoverWidth;
          break;
        case 'center':
        default:
          x = adjustedTriggerLeft + (triggerWidth - popoverWidth) / 2;
      }
      y =
        placement === 'top'
          ? adjustedTriggerTop - popoverHeight - offsetY
          : adjustedTriggerTop + triggerHeight + offsetY;
      break;
    }
    case 'left':
    case 'right': {
      switch (align) {
        case 'start':
          y = adjustedTriggerTop;
          break;
        case 'end':
          y = adjustedTriggerTop + triggerHeight - popoverHeight;
          break;
        case 'center':
        default:
          y = adjustedTriggerTop + (triggerHeight - popoverHeight) / 2;
      }
      x =
        placement === 'left'
          ? adjustedTriggerLeft - popoverWidth - offsetX
          : adjustedTriggerLeft + triggerWidth + offsetX;
      break;
    }
  }

  return { x, y };
};

const oppositePlacement: Record<Placement, Placement> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

interface SpaceContext {
  triggerRect: Rect;
  viewportWidth: number;
  viewportHeight: number;
  viewportMargin: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Room between the trigger's edge on `side` and the viewport edge, in viewport
 * coordinates, after reserving the margin and the gap the offset introduces.
 * Negative when the trigger itself sits inside the margin.
 */
const getSpaceOnSide = (side: Placement, context: SpaceContext): number => {
  const {
    triggerRect,
    viewportWidth,
    viewportHeight,
    viewportMargin,
    offsetX,
    offsetY,
  } = context;

  switch (side) {
    case 'top':
      return triggerRect.top - viewportMargin - offsetY;
    case 'bottom':
      return viewportHeight - triggerRect.bottom - viewportMargin - offsetY;
    case 'left':
      return triggerRect.left - viewportMargin - offsetX;
    case 'right':
      return viewportWidth - triggerRect.right - viewportMargin - offsetX;
  }
};

/**
 * Picks the side the popover ends up on. The requested side wins whenever it fits;
 * otherwise the opposite side wins if it fits; otherwise whichever has more room,
 * with ties keeping the requested side.
 */
const resolvePlacement = (
  requested: Placement,
  requestedSpace: number,
  oppositeSpace: number,
  mainAxisSize: number,
): Placement => {
  if (requestedSpace >= mainAxisSize) return requested;
  if (oppositeSpace >= mainAxisSize) return oppositePlacement[requested];
  return oppositeSpace > requestedSpace
    ? oppositePlacement[requested]
    : requested;
};

/**
 * Applies viewport constraints to the initial position.
 *
 * Two behaviours:
 * 1. Best-fit flipping on the main axis: the popover moves to the opposite side
 *    (top↔bottom, left↔right) when the requested side cannot hold it but the opposite
 *    side can. When neither side can, it goes to the side with more room. The
 *    popover always sits flush against the trigger's far edge (plus `offset`) on the
 *    side it lands on. `lockPlacement` skips this step.
 * 2. Shifting on the cross axis: the popover slides along the trigger so it stays
 *    within the viewport margins, which may break the requested alignment.
 *
 * The main axis is never clamped to the viewport. Doing so can only move the popover
 * toward, and then over, the trigger — which is exactly the overlap a hover-opened
 * surface cannot survive (the pointer leaves the trigger, the surface closes, the
 * pointer is back on the trigger, it reopens). A popover bigger than the room on its
 * side therefore overflows the far viewport edge instead; `available` tells the
 * consumer how much room there is so it can cap the popover's size to prevent that.
 *
 * @param position - The initial calculated position in document coordinates
 * @param popoverRect - The bounding rectangle of the popover element
 * @param triggerRect - The bounding rectangle of the trigger element
 * @param placement - The original intended placement
 * @param options - Additional configuration options
 * @returns The adjusted position, flip information and available space
 */
export const applyConstraintsToPopover = (
  position: Position,
  popoverRect: Rect,
  triggerRect: Rect,
  placement: Placement,
  options: PositioningOptions = {},
): PositioningResult => {
  const { offset = 0, viewportMargin = 8, lockPlacement = false } = options;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  const { width: popoverWidth, height: popoverHeight } = popoverRect;
  const { x: offsetX, y: offsetY } = getOffsetValues(offset);

  const spaceContext: SpaceContext = {
    triggerRect,
    viewportWidth,
    viewportHeight,
    viewportMargin,
    offsetX,
    offsetY,
  };

  const isVertical = placement === 'top' || placement === 'bottom';
  const mainAxisSize = isVertical ? popoverHeight : popoverWidth;

  const finalPlacement = lockPlacement
    ? placement
    : resolvePlacement(
        placement,
        getSpaceOnSide(placement, spaceContext),
        getSpaceOnSide(oppositePlacement[placement], spaceContext),
        mainAxisSize,
      );
  const flippedPlacement = finalPlacement === placement ? null : finalPlacement;

  let { x, y } = position;

  if (flippedPlacement) {
    switch (flippedPlacement) {
      case 'bottom':
        y = triggerRect.bottom + scrollY + offsetY;
        break;
      case 'top':
        y = triggerRect.top + scrollY - popoverHeight - offsetY;
        break;
      case 'right':
        x = triggerRect.right + scrollX + offsetX;
        break;
      case 'left':
        x = triggerRect.left + scrollX - popoverWidth - offsetX;
        break;
    }
  }

  if (isVertical) {
    x = Math.max(
      scrollX + viewportMargin,
      Math.min(x, scrollX + viewportWidth - popoverWidth - viewportMargin),
    );
  } else {
    y = Math.max(
      scrollY + viewportMargin,
      Math.min(y, scrollY + viewportHeight - popoverHeight - viewportMargin),
    );
  }

  const mainAxisSpace = Math.max(
    0,
    getSpaceOnSide(finalPlacement, spaceContext),
  );
  const available = isVertical
    ? {
        width: Math.max(0, viewportWidth - 2 * viewportMargin),
        height: mainAxisSpace,
      }
    : {
        width: mainAxisSpace,
        height: Math.max(0, viewportHeight - 2 * viewportMargin),
      };

  return {
    position: { x, y },
    flip: {
      placement: flippedPlacement,
    },
    available,
  };
};

/**
 * Complete popover positioning function that combines initial positioning
 * and viewport constraints in one call.
 *
 * This is the main entry point for popover positioning. It first calculates the ideal position
 * based on placement and alignment preferences, then applies viewport constraints to ensure
 * the popover remains visible and accessible.
 *
 * @param triggerRect - The bounding rectangle of the trigger element
 * @param popoverRect - The bounding rectangle of the popover element
 * @param placement - Where to place the popover relative to the trigger
 * @param align - How to align the popover with the trigger
 * @param options - Additional configuration options
 * @returns The final position, flip information and available space for the popover
 */
export const positionPopover = (
  triggerRect: Rect,
  popoverRect: Rect,
  placement: Placement,
  align: Alignment,
  options: PositioningOptions = {},
): PositioningResult => {
  const initialPosition = calculateInitialPositionOfPopover(
    triggerRect,
    popoverRect,
    placement,
    align,
    options,
  );

  return applyConstraintsToPopover(
    initialPosition,
    popoverRect,
    triggerRect,
    placement,
    options,
  );
};

/**
 * Computes the arrow's cross-axis offset inside the popover and clamps it to the popover's bounds.
 * Use after final positioning (including flips/clamping) so the arrow points at the trigger
 */
export type ComputeArrowOffsetParams = {
  triggerRect: Rect;
  popoverRect: Rect;
  position: Position;
  finalPlacement: Placement;
};

export const computeArrowOffset = ({
  triggerRect,
  popoverRect,
  position,
  finalPlacement,
}: ComputeArrowOffsetParams): number => {
  const triggerCenterX =
    triggerRect.left + triggerRect.width / 2 + window.scrollX;
  const triggerCenterY =
    triggerRect.top + triggerRect.height / 2 + window.scrollY;

  if (finalPlacement === 'top' || finalPlacement === 'bottom') {
    const raw = triggerCenterX - position.x; // x within popover coordinate space
    return Math.max(0, Math.min(raw, popoverRect.width));
  }

  // left | right
  const raw = triggerCenterY - position.y; // y within popover coordinate space
  return Math.max(0, Math.min(raw, popoverRect.height));
};
